/**
 * Which of a team's server names fits a name cell (Scores UX spec v1.0
 * section 1.4). Shared by web and mobile.
 *
 * A name is one line and is never ellipsized: a cut name costs the fan the
 * team. So instead of cutting, the row picks the longest server name that
 * fits: the full `name` ("Seattle Seahawks"), then `shortName` ("Seahawks",
 * when the server sends it), then `abbreviation` ("SEA"). The client never
 * builds a short name by splitting strings; it only chooses between the ones
 * the server sent. That is a layout decision, not data derivation.
 *
 * The width is an ESTIMATE, `chars * fontPx * 0.6`. 0.6em is a conservative
 * average advance for the system and Inter faces at weight 700, so a label the
 * estimate accepts fits at either weight. No DOM, no canvas, no Intl.
 */

/** The average advance, in em, the estimate assumes. */
export const TEAM_LABEL_EM_PER_CHAR = 0.6;

export type typeTeamLabelCandidates = {
  name: string;
  /** The nickname or school ("Chiefs", "Illinois"), or null until the server
   *  sends it. */
  shortName?: string | null | undefined;
  abbreviation: string;
};

/** The estimated rendered width of `label` at `fontPx`. */
export function estimateLabelWidthPx(label: string, fontPx: number): number {
  return label.length * fontPx * TEAM_LABEL_EM_PER_CHAR;
}

/**
 * The first candidate whose estimate fits `availablePx`. The abbreviation is
 * always the final answer, even when its estimate does not fit: at most five
 * characters, it fits every cell the row arithmetic can produce.
 */
export function pickTeamLabel(
  candidates: typeTeamLabelCandidates,
  availablePx: number,
  fontPx: number,
): string {
  const ordered = [candidates.name, candidates.shortName ?? ""].filter(
    (label) => label !== "",
  );
  for (const label of ordered) {
    if (estimateLabelWidthPx(label, fontPx) <= availablePx) {
      return label;
    }
  }
  return candidates.abbreviation !== ""
    ? candidates.abbreviation
    : candidates.name;
}
